/* eslint filenames/match-regex: off */
import {documentToReactComponents} from '@contentful/rich-text-react-renderer'
import {BLOCKS, INLINES} from '@contentful/rich-text-types'
import {ConsentExperience} from '@github-ui/consent-experience'
import {
  Box,
  Button,
  FormControl,
  Grid,
  Heading,
  InlineLink,
  Label,
  OrderedList,
  Select,
  Stack,
  Text,
  TextInput,
  Textarea,
  ThemeProvider,
  UnorderedList,
} from '@primer/react-brand'
import {Fragment, useCallback, useState} from 'react'
import {z} from 'zod'
import {enterpriseContactRequest} from '@github-ui/paths'
import {verifiedFetchJSON} from '@github-ui/verified-fetch'
import {isFeatureEnabled} from '@github-ui/feature-flags'

import {Octocaptcha, useOctocaptcha} from '../../components'
import {Flash} from './components/Flash'
import {EnterpriseBackground} from './components/Background/EnterpriseBackground'
import {countries} from './data/countries'
import {useConsentExperience} from './hooks/useConsentExperience'
import {useForm, type FormField} from './hooks/useForm'
import {useParams} from './hooks/useParams'
import {formFieldsMarketingFormsApi, formFieldsEloqua} from './constants'
import type {FormFieldsAttributes} from './types'
import type {EnterpriseContactPage} from '../../lib/types/contentful'

type EnterpriseContactTemplateProps = {
  page: EnterpriseContactPage
}

export function EnterpriseContactTemplate({page}: EnterpriseContactTemplateProps) {
  const {
    fields: {template},
  } = page
  const isMarketingFormsApi = isFeatureEnabled('MARKETING_FORMS_API_INTEGRATION_CONTACT_REQUEST')
  const queryParametersMap = useParams()
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined)
  const {isConsentExperienceValid, onConsentExperienceValidationChange} = useConsentExperience()
  const {octocaptchaToken, hasOctocaptchaFailedToLoad, onOctocaptchaComplete, onOctocaptchaLoadError} = useOctocaptcha()

  const onValidationMessageClick = useCallback((field: FormField) => {
    return (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
      event.preventDefault()

      const fieldEl = document.querySelector<HTMLElement>(`[name="${field.name}"]`)

      if (fieldEl === null) {
        return
      }

      fieldEl.focus()
    }
  }, [])

  const consentExperienceExamplesFields = isMarketingFormsApi
    ? ['full name', 'company', 'email']
    : ['first name', 'last name', 'company', 'email']

  const formFieldDefinitions: FormFieldsAttributes[] = [
    ...(isMarketingFormsApi ? formFieldsMarketingFormsApi : formFieldsEloqua),
    {
      label: 'Company',
      name: 'company',
      placeholder: 'e.g. Acme Inc.',
      required: true,
      type: 'text',
    },
    {
      label: 'Work Email',
      name: 'email',
      placeholder: 'e.g. name@company',
      required: true,
      type: 'email',
    },
    {
      label: 'Phone Number',
      name: 'phone',
      placeholder: 'If you want us to call you',
      required: false,
      type: 'text',
    },
    {
      label: 'Message',
      name: 'request_details',
      placeholder: 'Describe your project, needs, and timeline',
      required: false,
      type: 'text',
    },
    {
      label: 'Country',
      name: 'country',
      required: true,
      type: 'country',
    },
  ]

  const {fields, formState, handleSubmit, register, getFormValues} = useForm()

  const handleSubmitForm = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (!isConsentExperienceValid || (!octocaptchaToken && !hasOctocaptchaFailedToLoad)) return

    const formData = new FormData(event.currentTarget)
    const marketingEmailOptinForm = formData.get('marketing_email_opt_in')

    const result = await verifiedFetchJSON(enterpriseContactRequest(), {
      method: 'POST',
      body: {
        site_enterprise_contact_request: {
          // 'marketing_email_opt_in' needs to have a default value to submit to the controller
          marketing_email_opt_in: marketingEmailOptinForm ?? false,
          ...getFormValues(),
          ...queryParametersMap,
        },
        ['octocaptcha-token']: octocaptchaToken,
      },
    })

    if (result.ok) {
      window.location.href = `/enterprise/contact/thanks`
    } else {
      setErrorMessage('Unable to submit form. Please try again.')
    }
  }

  return (
    <ThemeProvider
      className="EnterpriseContact__Page position-relative"
      colorMode="dark"
      style={{backgroundColor: 'var(--brand-color-canvas-default)'}}
    >
      <Grid fullWidth className="EnterpriseContact__DesktopBackground height-full position-absolute p-0">
        <EnterpriseBackground />

        <Grid.Column span={6} start={7} className="position-relative">
          <ThemeProvider
            className="height-full"
            colorMode={page.fields.settings?.fields.colorMode ?? 'light'}
            style={{backgroundColor: 'var(--brand-color-canvas-default)'}}
          />
        </Grid.Column>
      </Grid>

      <Grid className="EnterpriseContact__Content position-relative p-0 mt-0">
        <Grid.Column span={{medium: 6}}>
          <Box
            padding={24}
            paddingBlockStart={{narrow: 64, regular: 128, wide: 48}}
            paddingBlockEnd={{narrow: 24, regular: 80}}
            marginBlockStart={{wide: 128}}
          >
            <Stack direction="vertical" gap="none" padding="none" alignItems="flex-start">
              <Stack direction="vertical" padding="none" gap={16} alignItems="flex-start">
                {template.fields.label && <Label>{template.fields.label}</Label>}

                <Heading as="h1" size="3">
                  {template.fields.headline}
                </Heading>
              </Stack>

              {template.fields.body ? (
                <Stack direction="vertical" gap={'none'} padding="none" alignItems="flex-start">
                  {template.fields.body &&
                    documentToReactComponents(template.fields.body, {
                      renderNode: {
                        [BLOCKS.HEADING_2]: (_, children) => (
                          <Heading as="h2" className="pb-2 pt-6" size="subhead-medium">
                            {children}
                          </Heading>
                        ),
                        [BLOCKS.PARAGRAPH]: (_, children) => <Text as="p">{children}</Text>,
                        [BLOCKS.UL_LIST]: (_, children) => <UnorderedList>{children}</UnorderedList>,
                        [BLOCKS.OL_LIST]: (_, children) => <OrderedList>{children}</OrderedList>,
                        [BLOCKS.LIST_ITEM]: (_, children) => <OrderedList.Item>{children}</OrderedList.Item>,
                        [INLINES.HYPERLINK]: (node, children) => (
                          <InlineLink href={node.data.uri}>{children}</InlineLink>
                        ),
                      },
                    })}
                </Stack>
              ) : null}
            </Stack>
          </Box>
        </Grid.Column>

        <Grid.Column span={{medium: 6}} start={{xlarge: 8}}>
          <ThemeProvider
            className="EnterpriseContact__Content__Right"
            colorMode={page.fields.settings?.fields.colorMode ?? 'light'}
            style={{backgroundColor: 'var(--brand-color-canvas-default)'}}
          >
            <Box
              padding={24}
              paddingBlockStart={{
                narrow: 64,
                regular: 128,
                wide: 48,
              }}
              paddingBlockEnd={{
                narrow: 24,
                regular: 80,
              }}
              marginBlockStart={{
                wide: 128,
              }}
            >
              <form
                noValidate
                onSubmit={handleSubmit(event => {
                  handleSubmitForm(event)
                })}
              >
                <Stack direction="vertical" padding="none" gap={24}>
                  <Stack gap={8} className="pb-3" padding="none">
                    <Text as="p" size="100">
                      All fields marked with an asterisk (*) are required.
                    </Text>
                  </Stack>

                  {formFieldDefinitions.map(definition => {
                    /**
                     * The field may be undefined for a little while until the field is registered.
                     */
                    const maybeField = fields[definition.name]

                    if (definition.type === 'country') {
                      return (
                        <FormControl
                          key={definition.name}
                          id="country"
                          validationStatus={maybeField?.validationStatus}
                          fullWidth
                          required={definition.required}
                        >
                          <FormControl.Label>{definition.label}</FormControl.Label>

                          <Select {...register('country', {label: definition.label, schema: z.string().min(1)})}>
                            <Select.Option value="" disabled>
                              Choose your country
                            </Select.Option>

                            {countries.map(country => (
                              <Select.Option key={country.value} value={country.value}>
                                {country.label}
                              </Select.Option>
                            ))}
                          </Select>

                          {maybeField?.validationStatus === 'error' ? (
                            <FormControl.Validation>{maybeField.error}</FormControl.Validation>
                          ) : (
                            <></>
                          )}
                        </FormControl>
                      )
                    }

                    if (definition.name === 'request_details') {
                      return (
                        <FormControl
                          key={definition.name}
                          validationStatus={maybeField?.validationStatus}
                          fullWidth
                          required={definition.required}
                        >
                          <FormControl.Label>{definition.label}</FormControl.Label>

                          <Textarea
                            {...register(definition.name, {label: definition.label, schema: z.string().optional()})}
                            placeholder={definition.placeholder}
                          />

                          {maybeField?.validationStatus === 'error' ? (
                            <FormControl.Validation>{maybeField.error}</FormControl.Validation>
                          ) : (
                            <></>
                          )}
                        </FormControl>
                      )
                    }

                    let validationSchema: z.ZodTypeAny

                    switch (definition.type) {
                      case 'number':
                      case 'text':
                        validationSchema = definition.required ? z.string().min(1) : z.string().optional()
                        break
                      case 'email':
                        validationSchema = definition.required
                          ? z.string().email().min(1)
                          : z.string().email().optional()
                        break
                      default:
                        validationSchema = z.any()
                    }

                    return (
                      <FormControl
                        key={definition.name}
                        validationStatus={maybeField?.validationStatus}
                        fullWidth
                        required={definition.required}
                      >
                        <FormControl.Label>{definition.label}</FormControl.Label>

                        <TextInput
                          {...register(definition.name, {label: definition.label, schema: validationSchema})}
                          placeholder={definition.placeholder}
                          type={definition.type}
                        />

                        {maybeField?.validationStatus === 'error' ? (
                          <FormControl.Validation>{maybeField.error}</FormControl.Validation>
                        ) : (
                          <></>
                        )}
                      </FormControl>
                    )
                  })}

                  <Stack direction="vertical" gap="condensed" padding="none">
                    <ConsentExperience
                      countryFieldSelector="#country"
                      emailSubscriptionSettingsLinkHref="/settings/emails/subscriptions/link-request/new"
                      exampleFields={consentExperienceExamplesFields}
                      fieldName="marketing_email_opt_in"
                      fieldValue="optInExplicit"
                      formControlClass="EnterpriseContact__Consent"
                      noticeClass="EnterpriseContact__Notice"
                      onValidationChange={onConsentExperienceValidationChange}
                      onlySendIfChecked={true}
                      privacyStatementHref="https://docs.github.com/articles/github-privacy-statement"
                    />

                    {!isConsentExperienceValid && (
                      <FormControl.Validation validationStatus="error">
                        You need to accept the required checkboxes to continue.
                      </FormControl.Validation>
                    )}
                  </Stack>

                  <FormControl required>
                    <FormControl.Label>Verify you&apos;re not a robot</FormControl.Label>

                    <Octocaptcha
                      originPage="marketing_forms"
                      onComplete={onOctocaptchaComplete}
                      onLoadError={onOctocaptchaLoadError}
                    />
                  </FormControl>

                  {Object.entries(queryParametersMap).map(([name, value]) => (
                    <input key={name} type="hidden" name={name} value={value ?? 'undefined'} />
                  ))}

                  <Box marginBlockStart={16}>
                    <Button variant="primary" type="submit" block>
                      Contact Sales
                    </Button>
                  </Box>

                  {formState.hasErrors && (
                    <Flash variant="error">
                      <Text as="p" weight="semibold" size="200">
                        The following inputs have errors:
                      </Text>

                      <Text as="p" size="200">
                        {Object.values<FormField>(fields)
                          .filter(field => field.validationStatus === 'error')
                          .map((field, index, collection) => {
                            const isLastElement = index === collection.length - 1

                            return (
                              <Fragment key={field.name}>
                                <a
                                  href={`#${field.name}`}
                                  style={{color: 'currentColor', textDecoration: 'underline'}}
                                  onClick={onValidationMessageClick(field)}
                                >
                                  {field.label ?? field.name}
                                </a>

                                {!isLastElement && ', '}
                              </Fragment>
                            )
                          })}
                      </Text>
                    </Flash>
                  )}

                  {errorMessage && (
                    <Flash variant="error">
                      <Text as="p" weight="semibold" size="200">
                        {errorMessage}
                      </Text>
                    </Flash>
                  )}
                </Stack>
              </form>
            </Box>
          </ThemeProvider>
        </Grid.Column>
      </Grid>
    </ThemeProvider>
  )
}

try{ EnterpriseContactTemplate.displayName ||= 'EnterpriseContactTemplate' } catch {}